import { useState, useEffect, useCallback } from 'react';
import { Plus, X, Trash2, Pencil, Download, FileText, Upload } from 'lucide-react';
import { clientAPI, configAPI } from '@/services/api';
import { useAuth } from '@/hooks/useAuth';
import { t } from '@/i18n/translations';
import { formatApiError } from '@/lib/apiError';
import { toast } from '@/components/ui/sonner';
import { PageHeader } from '@/components/layout/PageHeader';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';

const EMPTY = {
  name: '',
  code: '',
  country: '',
  address: '',
  activity_sector_id: null,
  purchase_volume_eur: '',
  email_contacts: [],
  strategic_type: 'standard',
  growth_potential: 'medium',
  payment_terms_parts: '',
  payment_terms_tooling: '',
  notes: '',
  // Bloco financeiro (guardado por endpoint próprio)
  fin_liquidity_days: '',
  fin_liabilities_total: '',
  fin_equity: '',
  fin_ebitda_eur: '',
  fin_risk_rating: null,
};

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const numOrNull = (v) => {
  if (v === undefined || v === null || v === '') return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

const fmtEur = (v) =>
  v === null || v === undefined || v === ''
    ? '—'
    : new Intl.NumberFormat('pt-PT', { style: 'currency', currency: 'EUR' }).format(v);

export default function ClientsPage() {
  const { user, lang } = useAuth();
  const [clients, setClients] = useState([]);
  const [sectors, setSectors] = useState([]);
  const [functions, setFunctions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editingId, setEditingId] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [savingFin, setSavingFin] = useState(false);
  const [uploading, setUploading] = useState(false);

  const canCreate = Boolean(user?.is_admin || user?.permissions?.client_create);
  const canEditClient = Boolean(user?.is_admin || user?.permissions?.client_edit);
  const canEditFinancial = Boolean(user?.is_admin || user?.permissions?.client_financial_edit);

  useEffect(() => {
    Promise.all([
      clientAPI.list(),
      configAPI.getActivitySectors(),
      configAPI.getContactFunctions(),
    ])
      .then(([c, s, f]) => {
        setClients(c.data);
        setSectors(s.data);
        setFunctions(f.data);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const buildPayload = () => {
    if (!form.name) {
      toast.error(t('name_required_toast', lang));
      return null;
    }
    const cleanContacts = (form.email_contacts || []).filter(
      (c) => (c.name || '').trim() || (c.email || '').trim() || (c.phone || '').trim()
    );
    for (const c of cleanContacts) {
      if (c.email && !EMAIL_RE.test(c.email)) {
        toast.error(t('email_invalid_toast', lang));
        return null;
      }
    }
    return {
      name: form.name,
      code: form.code || null,
      country: form.country || '',
      address: form.address || '',
      activity_sector_id: form.activity_sector_id ? Number(form.activity_sector_id) : null,
      purchase_volume_eur: numOrNull(form.purchase_volume_eur),
      email_contacts: cleanContacts.map((c) => ({
        name: c.name || '',
        email: c.email || '',
        function: c.function || '',
        phone: c.phone || '',
      })),
      strategic_type: form.strategic_type,
      growth_potential: form.growth_potential,
      payment_terms_parts: form.payment_terms_parts || '',
      payment_terms_tooling: form.payment_terms_tooling || '',
      notes: form.notes || '',
    };
  };

  const closeForm = () => {
    setShowForm(false);
    setEditingId(null);
    setForm(EMPTY);
    setDocuments([]);
  };

  const loadDocuments = useCallback((clientId) => {
    clientAPI
      .listDocuments(clientId)
      .then((r) => setDocuments(r.data))
      .catch(() => setDocuments([]));
  }, []);

  const handleSave = async () => {
    const payload = buildPayload();
    if (!payload) return;
    if (editingId == null) {
      try {
        const res = await clientAPI.create(payload);
        setClients((prev) => [...prev, res.data]);
        // Passa a modo edição para permitir preencher o bloco financeiro/anexos.
        startEdit(res.data);
        toast.success(t('client_created', lang));
      } catch {
        toast.error(t('client_create_error', lang));
      }
    } else {
      try {
        const res = await clientAPI.update(editingId, payload);
        setClients((prev) => prev.map((c) => (c.id === editingId ? res.data : c)));
        setForm((p) => ({ ...p, ...mergeClientToForm(res.data) }));
        toast.success(t('client_updated', lang));
      } catch {
        toast.error(t('client_update_error', lang));
      }
    }
  };

  const mergeClientToForm = (c) => ({
    name: c.name || '',
    code: c.code || '',
    country: c.country || '',
    address: c.address || '',
    activity_sector_id: c.activity_sector_id ?? null,
    purchase_volume_eur: c.purchase_volume_eur ?? '',
    email_contacts: Array.isArray(c.email_contacts)
      ? c.email_contacts.map((x) => ({
          name: x.name || '',
          email: x.email || '',
          function: x.function || x.role || '',
          phone: x.phone || '',
        }))
      : [],
    strategic_type: c.strategic_type || 'standard',
    growth_potential: c.growth_potential || 'medium',
    payment_terms_parts: c.payment_terms_parts || '',
    payment_terms_tooling: c.payment_terms_tooling || '',
    notes: c.notes || '',
    fin_liquidity_days: c.fin_liquidity_days ?? '',
    fin_liabilities_total: c.fin_liabilities_total ?? '',
    fin_equity: c.fin_equity ?? '',
    fin_ebitda_eur: c.fin_ebitda_eur ?? '',
    fin_risk_rating: c.fin_risk_rating ?? null,
  });

  const startEdit = (c) => {
    setEditingId(c.id);
    setForm(mergeClientToForm(c));
    setShowForm(true);
    loadDocuments(c.id);
  };

  const startCreate = () => {
    setEditingId(null);
    setForm(EMPTY);
    setDocuments([]);
    setShowForm(true);
  };

  const set = (f) => (e) => {
    const value = typeof e === 'string' ? e : e.target.value;
    setForm((p) => ({ ...p, [f]: value }));
  };

  const setContactField = (idx, field) => (e) => {
    const value = typeof e === 'string' ? e : e.target.value;
    setForm((p) => {
      const list = [...(p.email_contacts || [])];
      list[idx] = { ...list[idx], [field]: value };
      return { ...p, email_contacts: list };
    });
  };

  const addContact = () => {
    setForm((p) => ({
      ...p,
      email_contacts: [...(p.email_contacts || []), { name: '', email: '', function: '', phone: '' }],
    }));
  };

  const removeContact = (idx) => {
    setForm((p) => ({
      ...p,
      email_contacts: (p.email_contacts || []).filter((_, i) => i !== idx),
    }));
  };

  const saveFinancial = async () => {
    if (editingId == null) return;
    setSavingFin(true);
    try {
      const res = await clientAPI.updateFinancial(editingId, {
        fin_liquidity_days: numOrNull(form.fin_liquidity_days),
        fin_liabilities_total: numOrNull(form.fin_liabilities_total),
        fin_equity: numOrNull(form.fin_equity),
        fin_ebitda_eur: numOrNull(form.fin_ebitda_eur),
        fin_risk_rating: form.fin_risk_rating ? Number(form.fin_risk_rating) : null,
      });
      setClients((prev) => prev.map((c) => (c.id === editingId ? res.data : c)));
      setForm((p) => ({ ...p, ...mergeClientToForm(res.data) }));
      toast.success(t('financial_saved', lang));
    } catch {
      toast.error(t('financial_save_error', lang));
    } finally {
      setSavingFin(false);
    }
  };

  const handleUpload = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file || editingId == null) return;
    setUploading(true);
    try {
      await clientAPI.uploadDocument(editingId, file);
      loadDocuments(editingId);
      toast.success(t('doc_attached', lang));
    } catch (err) {
      toast.error(formatApiError(err, t('error_attaching_doc', lang)));
    } finally {
      setUploading(false);
    }
  };

  const handleDownload = async (doc) => {
    try {
      const blob = await clientAPI.downloadDocument(editingId, doc.id);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = doc.filename || 'download';
      a.click();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error(t('download_error', lang));
    }
  };

  const handleDeleteDoc = async (doc) => {
    try {
      await clientAPI.deleteDocument(editingId, doc.id);
      setDocuments((prev) => prev.filter((d) => d.id !== doc.id));
    } catch {
      toast.error(t('delete_error', lang));
    }
  };

  const sectorName = (id) => sectors.find((s) => s.id === id)?.name || '—';

  const liveSolvency = (() => {
    const liab = numOrNull(form.fin_liabilities_total);
    const eq = numOrNull(form.fin_equity);
    if (liab === null || eq === null || eq === 0) return null;
    return liab / eq;
  })();

  return (
    <div className="space-y-5">
      <PageHeader
        kicker={t('app_title', lang)}
        title={t('clients', lang)}
        actions={canCreate ? (
          <Button onClick={() => (showForm ? closeForm() : startCreate())}>
            {showForm ? <X className="h-4 w-4" /> : <Plus className="h-4 w-4" />}
            {showForm ? t('cancel', lang) : t('new_client', lang)}
          </Button>
        ) : null}
      />

      {showForm && (
        <Card>
          <CardHeader>
            <CardTitle>{editingId == null ? t('new_client', lang) : t('edit_client', lang)}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
              <div className="space-y-1.5">
                <Label htmlFor="client-name">{t('name', lang)} *</Label>
                <Input id="client-name" value={form.name} onChange={set('name')} disabled={!canEditClient} />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="client-code">{t('internal_code', lang)}</Label>
                <Input id="client-code" value={form.code} onChange={set('code')} disabled={!canEditClient} />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="client-country">{t('country', lang)}</Label>
                <Input id="client-country" value={form.country} onChange={set('country')} disabled={!canEditClient} />
              </div>
              <div className="space-y-1.5">
                <Label>{t('activity_sector', lang)}</Label>
                <Select
                  value={form.activity_sector_id ? String(form.activity_sector_id) : ''}
                  onValueChange={(v) => set('activity_sector_id')(v)}
                  disabled={!canEditClient}
                >
                  <SelectTrigger>
                    <SelectValue placeholder={t('select_placeholder', lang)} />
                  </SelectTrigger>
                  <SelectContent>
                    {sectors.map((s) => (
                      <SelectItem key={s.id} value={String(s.id)}>
                        {s.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-1.5">
                <Label htmlFor="client-address">{t('address_field', lang)}</Label>
                <Textarea id="client-address" rows={2} value={form.address} onChange={set('address')} disabled={!canEditClient} />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="client-volume">{t('purchase_volume_eur', lang)}</Label>
                <Input
                  id="client-volume"
                  type="number"
                  min="0"
                  step="0.01"
                  placeholder="0.00"
                  value={form.purchase_volume_eur}
                  onChange={set('purchase_volume_eur')}
                  disabled={!canEditClient}
                />
              </div>
            </div>

            {/* Contactos da equipa do cliente (N elementos) */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label>{t('team_contacts', lang)}</Label>
                {canEditClient && (
                  <Button type="button" variant="outline" size="sm" onClick={addContact}>
                    <Plus className="h-3 w-3" /> {t('add_contact', lang)}
                  </Button>
                )}
              </div>
              {(form.email_contacts || []).length === 0 ? (
                <p className="text-xs text-muted-foreground">{t('no_extra_contacts', lang)}</p>
              ) : (
                <div className="space-y-2">
                  {(form.email_contacts || []).map((c, idx) => (
                    <div key={idx} className="grid gap-2 md:grid-cols-[1.2fr_1.4fr_1fr_1fr_auto]">
                      <Input
                        placeholder={t('name', lang)}
                        value={c.name || ''}
                        onChange={setContactField(idx, 'name')}
                        disabled={!canEditClient}
                      />
                      <Input
                        type="email"
                        placeholder="email@cliente.pt"
                        value={c.email || ''}
                        onChange={setContactField(idx, 'email')}
                        disabled={!canEditClient}
                      />
                      <Select
                        value={c.function || ''}
                        onValueChange={(v) => setContactField(idx, 'function')(v)}
                        disabled={!canEditClient}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder={t('function_field', lang)} />
                        </SelectTrigger>
                        <SelectContent>
                          {functions.map((f) => (
                            <SelectItem key={f.id} value={f.name}>
                              {f.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <Input
                        placeholder={t('phone_field', lang)}
                        value={c.phone || ''}
                        onChange={setContactField(idx, 'phone')}
                        disabled={!canEditClient}
                      />
                      {canEditClient && (
                        <Button type="button" variant="ghost" size="icon" onClick={() => removeContact(idx)}>
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-1.5">
                <Label>{t('strategic_type', lang)}</Label>
                <Select value={form.strategic_type} onValueChange={set('strategic_type')} disabled={!canEditClient}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="strategic">{t('strategic', lang)}</SelectItem>
                    <SelectItem value="standard">{t('standard', lang)}</SelectItem>
                    <SelectItem value="transactional">{t('transactional', lang)}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>{t('growth_potential', lang)}</Label>
                <Select value={form.growth_potential} onValueChange={set('growth_potential')} disabled={!canEditClient}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="high">{t('high', lang)}</SelectItem>
                    <SelectItem value="medium">{t('medium', lang)}</SelectItem>
                    <SelectItem value="low">{t('low', lang)}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Payment terms */}
            <div className="space-y-2 rounded-md border border-border/70 bg-muted/30 p-3">
              <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                {t('payment_terms_section', lang)}
              </div>
              <p className="text-xs text-muted-foreground">{t('payment_terms_help', lang)}</p>
              <div className="grid gap-3 md:grid-cols-2">
                <div className="space-y-1.5">
                  <Label htmlFor="client-pt-parts">{t('payment_terms_parts', lang)}</Label>
                  <Textarea id="client-pt-parts" rows={2} value={form.payment_terms_parts} onChange={set('payment_terms_parts')} disabled={!canEditClient} />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="client-pt-tooling">{t('payment_terms_tooling', lang)}</Label>
                  <Textarea id="client-pt-tooling" rows={2} value={form.payment_terms_tooling} onChange={set('payment_terms_tooling')} disabled={!canEditClient} />
                </div>
              </div>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="client-notes">{t('notes', lang)}</Label>
              <Textarea id="client-notes" value={form.notes} onChange={set('notes')} disabled={!canEditClient} />
            </div>

            {canEditClient && (
              <div className="flex gap-2">
                <Button onClick={handleSave}>
                  {editingId == null ? t('create', lang) : t('save', lang)}
                </Button>
                <Button variant="outline" onClick={closeForm}>
                  {t('cancel', lang)}
                </Button>
              </div>
            )}

            {/* ===================== BLOCO FINANCEIRO ===================== */}
            {editingId != null && (
              <div className="space-y-3 rounded-md border border-amber-300/60 bg-amber-50/40 p-3 dark:border-amber-800/50 dark:bg-amber-950/20">
                <div className="flex items-center justify-between">
                  <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                    {t('financial_health', lang)}
                  </div>
                  {!canEditFinancial && (
                    <span className="text-xs text-muted-foreground">{t('read_only', lang)}</span>
                  )}
                </div>
                <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
                  <div className="space-y-1.5">
                    <Label htmlFor="fin-liq">{t('liquidity_days', lang)}</Label>
                    <Input id="fin-liq" type="number" min="0" step="1" value={form.fin_liquidity_days} onChange={set('fin_liquidity_days')} disabled={!canEditFinancial} />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="fin-liab">{t('liabilities_total', lang)}</Label>
                    <Input id="fin-liab" type="number" step="0.01" value={form.fin_liabilities_total} onChange={set('fin_liabilities_total')} disabled={!canEditFinancial} />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="fin-eq">{t('equity', lang)}</Label>
                    <Input id="fin-eq" type="number" step="0.01" value={form.fin_equity} onChange={set('fin_equity')} disabled={!canEditFinancial} />
                  </div>
                  <div className="space-y-1.5">
                    <Label>{t('solvency_ratio', lang)}</Label>
                    <div className="flex h-9 items-center rounded-md border border-input bg-muted/40 px-3 text-sm">
                      {liveSolvency === null ? '—' : liveSolvency.toFixed(2)}
                    </div>
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="fin-ebitda">{t('ebitda_eur', lang)}</Label>
                    <Input id="fin-ebitda" type="number" step="0.01" value={form.fin_ebitda_eur} onChange={set('fin_ebitda_eur')} disabled={!canEditFinancial} />
                  </div>
                  <div className="space-y-1.5">
                    <Label>{t('risk_rating', lang)}</Label>
                    <Select
                      value={form.fin_risk_rating ? String(form.fin_risk_rating) : ''}
                      onValueChange={(v) => set('fin_risk_rating')(v)}
                      disabled={!canEditFinancial}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder={t('select_placeholder', lang)} />
                      </SelectTrigger>
                      <SelectContent>
                        {[1, 2, 3, 4, 5].map((n) => (
                          <SelectItem key={n} value={String(n)}>
                            {n} — {t(`risk_${n}`, lang)}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* Relatórios financeiros (anexos) */}
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label>{t('financial_reports', lang)}</Label>
                    {canEditFinancial && (
                      <label className="inline-flex cursor-pointer items-center gap-1 rounded-md border border-input px-3 py-1.5 text-sm hover:bg-accent">
                        <Upload className="h-3.5 w-3.5" />
                        {uploading ? t('uploading', lang) : t('upload_report', lang)}
                        <input type="file" className="hidden" onChange={handleUpload} disabled={uploading} />
                      </label>
                    )}
                  </div>
                  {documents.length === 0 ? (
                    <p className="text-xs text-muted-foreground">{t('no_documents', lang)}</p>
                  ) : (
                    <div className="space-y-1">
                      {documents.map((d) => (
                        <div key={d.id} className="flex items-center justify-between rounded-md border border-border/60 bg-background px-3 py-1.5 text-sm">
                          <span className="inline-flex items-center gap-2 truncate">
                            <FileText className="h-3.5 w-3.5 shrink-0" />
                            <span className="truncate">{d.filename}</span>
                          </span>
                          <span className="flex items-center gap-1">
                            <Button size="icon" variant="ghost" onClick={() => handleDownload(d)}>
                              <Download className="h-3.5 w-3.5" />
                            </Button>
                            {canEditFinancial && (
                              <Button size="icon" variant="ghost" onClick={() => handleDeleteDoc(d)}>
                                <Trash2 className="h-3.5 w-3.5" />
                              </Button>
                            )}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {canEditFinancial && (
                  <div className="flex gap-2">
                    <Button onClick={saveFinancial} disabled={savingFin}>
                      {savingFin ? t('saving', lang) : t('save_financial', lang)}
                    </Button>
                    {!canEditClient && (
                      <Button variant="outline" onClick={closeForm}>
                        {t('close', lang)}
                      </Button>
                    )}
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      <Card className="p-0">
        {loading ? (
          <div className="space-y-2 p-4">
            <Skeleton className="h-10" />
            <Skeleton className="h-10" />
            <Skeleton className="h-10" />
          </div>
        ) : clients.length === 0 ? (
          <p className="p-6 text-center text-sm text-muted-foreground">{t('no_clients', lang)}</p>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t('name', lang)}</TableHead>
                <TableHead>{t('internal_code', lang)}</TableHead>
                <TableHead>{t('country', lang)}</TableHead>
                <TableHead>{t('activity_sector', lang)}</TableHead>
                <TableHead>{t('type', lang)}</TableHead>
                <TableHead className="text-right">{t('purchase_volume_eur', lang)}</TableHead>
                <TableHead>{t('financial_health', lang)}</TableHead>
                <TableHead className="w-[100px]">{t('actions', lang)}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {clients.map((c) => (
                <TableRow key={c.id}>
                  <TableCell className="font-medium">{c.name}</TableCell>
                  <TableCell>{c.code || '—'}</TableCell>
                  <TableCell>{c.country || '—'}</TableCell>
                  <TableCell>{c.activity_sector_name || sectorName(c.activity_sector_id)}</TableCell>
                  <TableCell>
                    <Badge variant="outline">{t(c.strategic_type, lang)}</Badge>
                  </TableCell>
                  <TableCell className="text-right">{fmtEur(c.purchase_volume_eur)}</TableCell>
                  <TableCell>
                    {c.fin_complete ? (
                      <Badge variant="outline" className="border-green-500/50 text-green-700 dark:text-green-400">
                        {t('financial_complete', lang)}
                      </Badge>
                    ) : (
                      <Badge variant="outline" className="border-amber-500/50 text-amber-700 dark:text-amber-400">
                        {t('financial_pending', lang)}
                      </Badge>
                    )}
                  </TableCell>
                  <TableCell>
                    <Button size="sm" variant="ghost" onClick={() => startEdit(c)}>
                      <Pencil className="h-3.5 w-3.5" /> {canEditClient ? t('edit', lang) : t('open', lang)}
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Card>
    </div>
  );
}
